import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import AiProviderFields from "./AiProviderFields";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Accordion,
  ListGroup,
  Badge,
  Pagination,
  InputGroup,
  Modal,
  Table,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const getLevelNameFromId = (id) => {
  switch (id) {
    case 7:
      return "G";
    case 1:
      return "F";
    case 2:
      return "E";
    case 3:
      return "D";
    case 4:
      return "C";
    case 5:
      return "B";
    case 6:
      return "A";
    default:
      return `ID ${id}`;
  }
};

const formatVnd = (value) => Number(value || 0).toLocaleString("vi-VN");

const EMPTY_TEAM_FORM = {
  name: "",
  start_date: "",
  location: "",
  level_ids: [],
  max_teams: 8,
  min_team_members: 3,
  max_team_members: 5,
  max_submatches: 5,
  teams_per_group: 4,
};

const TournamentManagement = () => {
  const [prompt, setPrompt] = useState("");
  const [aiConfig, setAiConfig] = useState({
    base_url: "",
    api_key: "",
    model: "",
    json_mode: 1,
  });
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]); // State to hold players list
  const [levels, setLevels] = useState([]);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [teamForm, setTeamForm] = useState(EMPTY_TEAM_FORM);
  const [editingTeamTournament, setEditingTeamTournament] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ type: "", msg: "" });
  const [needsRefresh, setNeedsRefresh] = useState(false);

  // State for the Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [editingTournamentContent, setEditingTournamentContent] = useState("");
  const [schedules, setSchedules] = useState({}); // State to hold schedules, keyed by tournament ID
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    bank_name: "",
    bank_code: "",
    account_number: "",
    account_name: "",
    fee_single: 0,
    fee_double: 0,
    fee_team: 0,
  });


  // States for search and pagination, keyed by tournament ID
  const [searchTerms, setSearchTerms] = useState({});
  const [currentPages, setCurrentPages] = useState({});
  const itemsPerPage = 5; // Show 5 registrations per page in the admin view

  // Create a map for quick player lookup
  const playerMap = new Map(players.map((p) => [p.id, p.name]));

  // Years available based on the tournament match date (start_date)
  const tournamentYears = useMemo(() => {
    const years = new Set([currentYear]);
    tournaments.forEach((t) => {
      if (!t.start_date) return;
      const year = new Date(t.start_date).getFullYear();
      if (!Number.isNaN(year)) years.add(year);
    });
    return [...years].sort((a, b) => b - a);
  }, [tournaments, currentYear]);

  const visibleTournaments = useMemo(() => {
    return tournaments
      .filter((t) => {
        if (!t.start_date) return false;
        const year = new Date(t.start_date).getFullYear();
        return !Number.isNaN(year) && year === Number(selectedYear);
      })
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  }, [tournaments, selectedYear]);

  const fetchData = useCallback(async () => {
    try {
      const [tourneyRes, playersRes, paymentRes] = await Promise.all([
        axios.get(`${API_URL}/tournaments/all-json`),
        axios.get(`${API_URL}/players?limit=all`),
        axios.get(`${API_URL}/tournaments/payment-settings`),
      ]);
      setTournaments(tourneyRes.data);
      setPlayers(playersRes.data);
      setPaymentForm(paymentRes.data);
      try {
        const levelsRes = await axios.get(`${API_URL}/team-tournaments/levels`);
        setLevels(levelsRes.data);
      } catch (levelsErr) {
        console.error("Error fetching levels:", levelsErr);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setNotification({
        type: "danger",
        msg: "Không thể tải dữ liệu cần thiết.",
      });
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, needsRefresh]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const res = await axios.post(`${API_URL}/tournaments/generate-with-ai`, {
        prompt,
        ai_config: aiConfig,
      });
      setNotification({
        type: "success",
        msg: `Tạo giải đấu thành công! Link đăng ký: ${getRegistrationLink(res.data.tournamentId)}`,
      });
      setPrompt("");
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg:
          err.response?.data?.message ||
          err.response?.data ||
          "Lỗi khi tạo giải đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegistration = async (tournamentId, player1Id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa đăng ký này?")) {
      try {
        await axios.delete(`${API_URL}/tournaments/${tournamentId}/register`, {
          data: { player_1_id: player1Id },
        });
        setNotification({
          type: "success",
          msg: "Đã xóa đăng ký thành công.",
        });
        setNeedsRefresh((p) => !p);
      } catch (err) {
        setNotification({
          type: "danger",
          msg: err.response?.data || "Lỗi khi xóa đăng ký.",
        });
      }
    }
  };

  const handleGenerateDraw = async (tournamentId) => {
    if (
      !window.confirm(
        `Bạn có chắc chắn muốn chia bảng và tạo lịch thi đấu cho giải đấu này không? Một giải đấu mới sẽ được tạo tự động dựa trên tên file. Thao tác này không thể hoàn tác.`,
      )
    ) {
      return;
    }

    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      await axios.post(`${API_URL}/tournaments/${tournamentId}/generate-draw`);
      setNotification({
        type: "success",
        msg: "Chia bảng và tạo lịch thi đấu thành công!",
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi thực hiện.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeamTournament = async (e) => {
    e.preventDefault();
    if (!teamForm.start_date) {
      setNotification({
        type: "warning",
        msg: "Vui lòng chọn ngày thi đấu cho giải đồng đội.",
      });
      return;
    }
    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_URL}/team-tournaments`, teamForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotification({
        type: "success",
        msg: `Đã tạo giải đồng đội. Link đăng ký: ${getRegistrationLink(res.data.uuid)}`,
      });
      setTeamForm(EMPTY_TEAM_FORM);
      setShowTeamForm(false);
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi tạo giải đồng đội.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTeamGroups = async (uuid) => {
    if (
      !window.confirm(
        "Chia bảng cho giải đồng đội này? Thao tác này không thể hoàn tác.",
      )
    ) {
      return;
    }
    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/team-tournaments/${uuid}/generate-groups`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotification({
        type: "success",
        msg: `Đã chia ${res.data.groups} bảng với ${res.data.ties} trận đội.`,
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi chia bảng.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeamTournament = async (uuid) => {
    if (
      !window.confirm(
        "Xoá giải đồng đội này cùng toàn bộ đội và trận đã nhập? Thao tác này không thể hoàn tác.",
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/team-tournaments/${uuid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotification({ type: "success", msg: "Đã xoá giải đồng đội." });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi xoá giải đồng đội.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/tournaments/payment-settings`, paymentForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotification({ type: "success", msg: "Đã lưu cấu hình thanh toán." });
      setShowPaymentForm(false);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi lưu cấu hình thanh toán.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTeamStructure = async (e) => {
    e.preventDefault();
    if (!editingTeamTournament) return;

    const structureEditable = editingTeamTournament.status === "registration_open";
    const payload = {
      name: editingTeamTournament.name,
      start_date: editingTeamTournament.start_date,
      location: editingTeamTournament.location,
      level_ids: editingTeamTournament.level_ids,
    };
    if (structureEditable) {
      Object.assign(payload, {
        max_teams: Number(editingTeamTournament.max_teams),
        min_team_members: Number(editingTeamTournament.min_team_members),
        max_team_members: Number(editingTeamTournament.max_team_members),
        max_submatches: Number(editingTeamTournament.max_submatches),
        teams_per_group: Number(editingTeamTournament.teams_per_group),
      });
    }

    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/team-tournaments/${editingTeamTournament.uuid}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotification({ type: "success", msg: "Đã cập nhật cấu trúc giải đồng đội." });
      setEditingTeamTournament(null);
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi cập nhật cấu trúc giải.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShowEditModal = async (tournamentId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/tournaments/${tournamentId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditingTournament(res.data);
      setEditingTournamentContent(JSON.stringify(res.data, null, 2));
      setShowEditModal(true);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi tải dữ liệu giải đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingTournament(null);
    setEditingTournamentContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingTournament) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const updatedTournamentData = JSON.parse(editingTournamentContent);
      await axios.put(`${API_URL}/tournaments/${editingTournament.id}`, updatedTournamentData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      handleCloseEditModal();
      setNotification({ type: "success", msg: "Đã cập nhật giải đấu thành công." });
      setNeedsRefresh(p => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi lưu giải đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTournament = async (tournamentId) => {
    if (
      !window.confirm(
        "Bạn có chắc chắn muốn xóa TOÀN BỘ giải đấu này không? Hành động này không thể hoàn tác.",
      )
    ) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/tournaments/${tournamentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotification({
        type: "success",
        msg: "Đã xóa giải đấu thành công.",
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi xóa giải đấu.",
      });
    }
  };

  const handleFetchSchedule = async (tournamentId) => {
    // Avoid refetching if already loaded
    if (schedules[tournamentId]) {
      // Toggle visibility by setting it to null/undefined
      setSchedules(prev => ({ ...prev, [tournamentId]: null }));
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/tournaments/${tournamentId}/schedule`);
      setSchedules(prev => ({ ...prev, [tournamentId]: res.data }));
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi tải lịch thi đấu.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleApproval = async (tournamentId, player1Id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/tournaments/${tournamentId}/register/approve`,
        { player_1_id: player1Id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotification({
        type: "success",
        msg: "Cập nhật trạng thái thành công.",
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi cập nhật.",
      });
    }
  };

  const getRegistrationLink = (uuid) => {
    const base = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://bongbanhadong.com").replace(
      /\/$/,
      ""
    );
    return `${base}/register-tournament/${uuid}`;
  };

  return (
    <Container>
      <Row>
        <Col>
          <h2>Quản lý Giải đấu Cá nhân</h2>
        </Col>
      </Row>

      {notification.msg && (
        <Alert
          variant={notification.type}
          onClose={() => setNotification({ type: "", msg: "" })}
          dismissible
        >
          {notification.msg}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Tạo Yêu cầu Bốc thăm (AI)</Card.Title>
          <Form onSubmit={handleGenerate}>
            <AiProviderFields value={aiConfig} onChange={setAiConfig} disabled={loading} />
            <Form.Group className="mb-3">
              <Form.Label>Nhập yêu cầu để tạo file đăng ký</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Ví dụ: Tạo giải đơn hạng A cho 32 vận động viên"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                required
              />
            </Form.Group>
            <Button type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Tạo Giải"}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0">Cấu hình thanh toán & lệ phí</Card.Title>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowPaymentForm((open) => !open)}
            >
              {showPaymentForm ? "Đóng" : "Sửa"}
            </Button>
          </div>

          {!showPaymentForm && (
            <div className="text-muted small mt-2">
              <div>
                <strong>{paymentForm.bank_name}</strong> · {paymentForm.account_number} ·{" "}
                {paymentForm.account_name} ({paymentForm.bank_code})
              </div>
              <div>
                Lệ phí: đơn {formatVnd(paymentForm.fee_single)}đ · đôi{" "}
                {formatVnd(paymentForm.fee_double)}đ · đồng đội{" "}
                {formatVnd(paymentForm.fee_team)}đ
              </div>
            </div>
          )}

          {showPaymentForm && (
            <Form className="mt-3" onSubmit={handleSavePayment}>
              <Row className="g-2">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Tên ngân hàng</Form.Label>
                    <Form.Control
                      required
                      value={paymentForm.bank_name}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, bank_name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Mã ngân hàng</Form.Label>
                    <Form.Control
                      required
                      value={paymentForm.bank_code}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, bank_code: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Số tài khoản</Form.Label>
                    <Form.Control
                      required
                      value={paymentForm.account_number}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, account_number: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Chủ tài khoản</Form.Label>
                    <Form.Control
                      required
                      value={paymentForm.account_name}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, account_name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2 mt-1">
                {[
                  ["fee_single", "Lệ phí đơn (đ)"],
                  ["fee_double", "Lệ phí đôi (đ)"],
                  ["fee_team", "Lệ phí đồng đội (đ)"],
                ].map(([field, label]) => (
                  <Col md={4} key={field}>
                    <Form.Group>
                      <Form.Label>{label}</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        step={1000}
                        value={paymentForm[field]}
                        onChange={(e) =>
                          setPaymentForm({
                            ...paymentForm,
                            [field]: Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
              <Button type="submit" className="mt-3" disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Lưu cấu hình"}
              </Button>
            </Form>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0">Giải đồng đội ngắn ngày</Card.Title>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowTeamForm((open) => !open)}
            >
              {showTeamForm ? "Đóng" : "Tạo giải đồng đội"}
            </Button>
          </div>
          {showTeamForm && (
            <Form className="mt-3" onSubmit={handleCreateTeamTournament}>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Tên giải</Form.Label>
                    <Form.Control
                      required
                      value={teamForm.name}
                      onChange={(e) =>
                        setTeamForm({ ...teamForm, name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Ngày thi đấu</Form.Label>
                    <Form.Control
                      type="date"
                      required
                      value={teamForm.start_date}
                      onChange={(e) =>
                        setTeamForm({ ...teamForm, start_date: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Địa điểm</Form.Label>
                    <Form.Control
                      value={teamForm.location}
                      onChange={(e) =>
                        setTeamForm({ ...teamForm, location: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-2">
                <Form.Label>Hạng tham gia</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {levels.map((level) => (
                    <Form.Check
                      key={level.id}
                      type="checkbox"
                      id={`team-level-${level.id}`}
                      label={level.name}
                      checked={teamForm.level_ids.includes(level.id)}
                      onChange={(e) => {
                        const ids = e.target.checked
                          ? [...teamForm.level_ids, level.id]
                          : teamForm.level_ids.filter((id) => id !== level.id);
                        setTeamForm({ ...teamForm, level_ids: ids });
                      }}
                    />
                  ))}
                </div>
              </Form.Group>

              <Row className="g-2 mt-1">
                {[
                  ["max_teams", "Số đội tối đa"],
                  ["min_team_members", "Thành viên tối thiểu"],
                  ["max_team_members", "Thành viên tối đa"],
                  ["max_submatches", "Trận tối đa mỗi cặp"],
                  ["teams_per_group", "Đội mỗi bảng"],
                ].map(([field, label]) => (
                  <Col md={2} xs={6} key={field}>
                    <Form.Group>
                      <Form.Label className="small">{label}</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={teamForm[field]}
                        onChange={(e) =>
                          setTeamForm({
                            ...teamForm,
                            [field]: Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>

              <Button type="submit" className="mt-3" disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Tạo giải đồng đội"}
              </Button>
            </Form>
          )}
        </Card.Body>
      </Card>

      <h3 className="mt-5">Danh sách Giải đấu</h3>
      <Row className="g-2 align-items-center mb-3">
        <Col xs="auto">
          <Form.Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            aria-label="Chọn năm thi đấu"
          >
            {tournamentYears.map((year) => (
              <option key={year} value={year}>
                Năm {year}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col className="text-muted small">
          {visibleTournaments.length} giải có ngày thi đấu trong năm {selectedYear}
        </Col>
      </Row>
      {visibleTournaments.length === 0 && (
        <Alert variant="info">
          Không có giải đấu nào có ngày thi đấu trong năm {selectedYear}.
        </Alert>
      )}
      <Accordion>
        {visibleTournaments.map((tourney) => (
          <Accordion.Item key={tourney.id} eventKey={tourney.id}>
            <Accordion.Header>
              <span className="fw-bold me-3">{tourney.name}</span>
              {tourney.type === "team" ? (
                <>
                  <Badge bg="dark" className="me-2">Đồng đội</Badge>
                  <Badge bg="primary">
                    {tourney.team_count || 0} / {tourney.player_limit} đội
                  </Badge>
                </>
              ) : (
                <Badge bg="primary">
                  {tourney.registrations.length} / {tourney.player_limit} VĐV
                </Badge>
              )}
            </Accordion.Header>
            <Accordion.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>ID:</strong> {tourney.id}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Hạng:</strong>{" "}
                  {Array.isArray(tourney.level_ids)
                    ? tourney.level_ids.map(getLevelNameFromId).join(", ")
                    : "N/A"}{" "}
                  | <strong>Loại:</strong>{" "}
                  {tourney.type === "single"
                    ? "Đơn"
                    : tourney.type === "double"
                      ? "Đôi"
                      : "Đồng đội"}
                </ListGroup.Item>
                {tourney.start_date && (
                  <ListGroup.Item>
                    <strong>Thời gian:</strong>{" "}
                    {new Date(tourney.start_date).toLocaleDateString("vi-VN")}
                  </ListGroup.Item>
                )}
                {tourney.location && (
                  <ListGroup.Item>
                    <strong>Địa điểm:</strong> {tourney.location}
                  </ListGroup.Item>
                )}
                <ListGroup.Item>
                  <strong>Link Đăng ký:</strong>{" "}
                  <a
                    href={getRegistrationLink(tourney.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getRegistrationLink(tourney.id)}
                  </a>
                </ListGroup.Item>
                {tourney.type !== "team" && (
                <ListGroup.Item>
                  <strong>VĐV đã đăng ký:</strong>
                  {(() => {
                    const searchTerm = searchTerms[tourney.id] || "";
                    const currentPage = currentPages[tourney.id] || 1;

					const filteredRegistrations = tourney.registrations.filter(
					  (reg) => {
						const p1Name =
						  playerMap.get(reg.player_1_id)?.toLowerCase() || "";
						const p2Name =
						  playerMap.get(reg.player_2_id)?.toLowerCase() || "";

						// Chuyển ID sang string để tìm kiếm
						const p1Id = reg.player_1_id.toString();
						const p2Id = reg.player_2_id ? reg.player_2_id.toString() : "";

						const search = searchTerm.toLowerCase();

						return (
						  p1Name.includes(search) ||
						  p2Name.includes(search) ||
						  p1Id.includes(searchTerm) ||
						  p2Id.includes(searchTerm)
						);
					  },
					);

                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = filteredRegistrations.slice(
                      indexOfFirstItem,
                      indexOfLastItem,
                    );
                    const totalPages = Math.ceil(
                      filteredRegistrations.length / itemsPerPage,
                    );

                    if (tourney.registrations.length === 0) {
                      return " Chưa có VĐV nào.";
                    }

                    return (
                      <>
                        <InputGroup className="my-3">
                          <Form.Control
                            placeholder="Tìm VĐV trong giải này..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerms({
                                ...searchTerms,
                                [tourney.id]: e.target.value,
                              });
                              setCurrentPages({
                                ...currentPages,
                                [tourney.id]: 1,
                              });
                            }}
                          />
                        </InputGroup>
                        {currentItems.length > 0 ? (
                          <ListGroup>
                            {currentItems.map((reg, index) => (
                              <ListGroup.Item
                                key={index}
                                className="d-flex justify-content-between align-items-center"
                              >
                                <span>
                                  <strong>
                                    {indexOfFirstItem + index + 1}.{" "}
                                  </strong>
                            {playerMap.get(reg.player_1_id)
							  ? `${playerMap.get(reg.player_1_id)} (ID: ${reg.player_1_id})`
							  : `ID: ${reg.player_1_id}`}{" "}
							(Hạng: {getLevelNameFromId(reg.player_1_level_id)})
							{reg.player_2_id && (
							  <>
								{` & ${
								  playerMap.get(reg.player_2_id)
									? `${playerMap.get(reg.player_2_id)} (ID: ${reg.player_2_id})`
									: `ID: ${reg.player_2_id}`
								} (Hạng: ${getLevelNameFromId(reg.player_2_level_id)})`}
							  </>
							)}

                                </span>
                                <div>
                                  <Badge
                                    bg={reg.approved ? "success" : "secondary"}
                                  >
                                    {reg.approved ? "Đã duyệt" : "Chưa duyệt"}
                                  </Badge>
                                  <Button
                                    variant={reg.approved ? "warning" : "info"}
                                    size="sm"
                                    className="mx-2"
                                    onClick={() =>
                                      handleToggleApproval(
                                        tourney.id,
                                        reg.player_1_id,
                                      )
                                    }
                                  >
                                    {reg.approved ? "Hủy duyệt" : "Duyệt"}
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteRegistration(
                                        tourney.id,
                                        reg.player_1_id,
                                      )
                                    }
                                  >
                                    Xóa
                                  </Button>
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <p>Không tìm thấy VĐV nào.</p>
                        )}
                        {totalPages > 1 && (
                          <Pagination className="mt-3 justify-content-center">
                            {[...Array(totalPages).keys()].map((number) => (
                              <Pagination.Item
                                key={number + 1}
                                active={number + 1 === currentPage}
                                onClick={() =>
                                  setCurrentPages({
                                    ...currentPages,
                                    [tourney.id]: number + 1,
                                  })
                                }
                              >
                                {number + 1}
                              </Pagination.Item>
                            ))}
                          </Pagination>
                        )}
                      </>
                    );
                  })()}
                </ListGroup.Item>
                )}

                {tourney.type === "team" && (
                  <ListGroup.Item>
                    <strong>Đội đã đăng ký:</strong> {tourney.team_count || 0} /{" "}
                    {tourney.player_limit} đội (đã duyệt:{" "}
                    {tourney.approved_team_count || 0})
                  </ListGroup.Item>
                )}

                {/* Schedule Display */}
                {schedules[tourney.id] && (
                  <ListGroup.Item>
                    <h5>Lịch thi đấu</h5>
                    <Table striped bordered hover responsive size="sm">
                      <thead>
                        <tr>
                          <th>Vòng</th>
                          <th>Đội 1</th>
                          <th>Đội 2</th>
                          <th>Kết quả</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules[tourney.id].map(match => (
                          <tr key={match.id}>
                            <td>{match.round_name}</td>
                            <td>
                              {playerMap.get(match.player_1_id) || `ID: ${match.player_1_id}`}
                              {match.player_2_id !== 0 &&
                                ` & ${playerMap.get(match.player_2_id) || `ID: ${match.player_2_id}`}`
                              }
                            </td>
                            <td>
                              {playerMap.get(match.player_3_id) || `ID: ${match.player_3_id}`}
                              {match.player_4_id !== 0 &&
                                ` & ${playerMap.get(match.player_4_id) || `ID: ${match.player_4_id}`}`
                              }
                            </td>
                            <td>{match.result || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ListGroup.Item>
                )}

                <ListGroup.Item className="text-end">
                  {tourney.type === "team" ? (
                    <>
                      <Button
                        variant="success"
                        onClick={() => handleGenerateTeamGroups(tourney.uuid)}
                        disabled={
                          loading ||
                          (tourney.approved_team_count || 0) < 3 ||
                          tourney.status !== "registration_open"
                        }
                      >
                        {loading && <Spinner size="sm" />} Chia bảng
                      </Button>
                      <Button
                        as={Link}
                        to={`/tournaments/${tourney.uuid}/teams`}
                        variant="info"
                        className="ms-2"
                      >
                        Quản lý trận đồng đội
                      </Button>
                      <Button
                        variant="secondary"
                        className="ms-2"
                        onClick={() =>
                          setEditingTeamTournament({
                            uuid: tourney.uuid,
                            name: tourney.name || "",
                            start_date: tourney.start_date || "",
                            location: tourney.location || "",
                            level_ids: tourney.level_ids || [],
                            max_teams: tourney.player_limit,
                            min_team_members: tourney.min_team_members,
                            max_team_members: tourney.max_team_members,
                            max_submatches: tourney.max_submatches,
                            teams_per_group: tourney.teams_per_group,
                            status: tourney.status,
                          })
                        }
                        disabled={loading}
                      >
                        Sửa cấu trúc
                      </Button>
                      <Button
                        variant="danger"
                        className="ms-2"
                        onClick={() => handleDeleteTeamTournament(tourney.uuid)}
                        disabled={loading}
                      >
                        Xóa Giải đấu
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        className="me-2"
                        onClick={() => handleFetchSchedule(tourney.id)}
                        disabled={loading || tourney.status !== 'draw_generated'}
                      >
                        {schedules[tourney.id] ? "Ẩn Lịch" : "Xem Lịch"}
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => handleGenerateDraw(tourney.id)}
                        disabled={
                          loading ||
                          tourney.registrations.length < 3 ||
                          tourney.status !== "registration_open"
                        }
                      >
                        {loading && <Spinner size="sm" />} Chia bảng & Tạo lịch
                      </Button>
                      <Button
                        variant="info"
                        className="ms-2"
                        onClick={() => handleShowEditModal(tourney.id)}
                        disabled={loading}
                      >
                        Chỉnh sửa
                      </Button>
                      <Button
                        variant="danger"
                        className="ms-2"
                        onClick={() => handleDeleteTournament(tourney.id)}
                        disabled={loading}
                      >
                        Xóa Giải đấu
                      </Button>
                    </>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {editingTeamTournament && (
        <Modal show size="lg" onHide={() => setEditingTeamTournament(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Sửa cấu trúc: {editingTeamTournament.name}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSaveTeamStructure}>
            <Modal.Body>
              {editingTeamTournament.status !== "registration_open" && (
                <Alert variant="warning">
                  Giải đã chia bảng: chỉ sửa được tên, ngày, địa điểm và hạng. Muốn đổi
                  số đội / đội mỗi bảng, vào trang quản lý trận đồng đội và xoá vòng bảng
                  trước.
                </Alert>
              )}
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tên giải</Form.Label>
                    <Form.Control
                      required
                      value={editingTeamTournament.name}
                      onChange={(e) =>
                        setEditingTeamTournament({
                          ...editingTeamTournament,
                          name: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Ngày thi đấu</Form.Label>
                    <Form.Control
                      type="date"
                      required
                      value={editingTeamTournament.start_date}
                      onChange={(e) =>
                        setEditingTeamTournament({
                          ...editingTeamTournament,
                          start_date: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Địa điểm</Form.Label>
                    <Form.Control
                      value={editingTeamTournament.location}
                      onChange={(e) =>
                        setEditingTeamTournament({
                          ...editingTeamTournament,
                          location: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-2">
                <Form.Label>Hạng tham gia</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {levels.map((level) => (
                    <Form.Check
                      key={level.id}
                      type="checkbox"
                      id={`edit-team-level-${level.id}`}
                      label={level.name}
                      checked={editingTeamTournament.level_ids.includes(level.id)}
                      onChange={(e) => {
                        const ids = e.target.checked
                          ? [...editingTeamTournament.level_ids, level.id]
                          : editingTeamTournament.level_ids.filter(
                              (id) => id !== level.id
                            );
                        setEditingTeamTournament({
                          ...editingTeamTournament,
                          level_ids: ids,
                        });
                      }}
                    />
                  ))}
                </div>
              </Form.Group>

              <Row className="g-2 mt-1">
                {[
                  ["max_teams", "Số đội tối đa"],
                  ["min_team_members", "Thành viên tối thiểu"],
                  ["max_team_members", "Thành viên tối đa"],
                  ["max_submatches", "Trận tối đa mỗi cặp"],
                  ["teams_per_group", "Đội mỗi bảng"],
                ].map(([field, label]) => (
                  <Col md={2} xs={6} key={field}>
                    <Form.Group>
                      <Form.Label className="small">{label}</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        disabled={editingTeamTournament.status !== "registration_open"}
                        value={editingTeamTournament[field]}
                        onChange={(e) =>
                          setEditingTeamTournament({
                            ...editingTeamTournament,
                            [field]: Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setEditingTeamTournament(null)}>
                Đóng
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Lưu"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {/* Edit Tournament Modal */}
      {editingTournament && (
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Chỉnh sửa Giải đấu: {editingTournament.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tên Giải đấu</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingTournament.name || ''}
                      onChange={(e) => {
                        const newName = e.target.value;
                        const updatedTournament = { ...editingTournament, name: newName };
                        setEditingTournament(updatedTournament);
                        setEditingTournamentContent(JSON.stringify(updatedTournament, null, 2));
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                   <Form.Group className="mb-3">
                    <Form.Label>Ngày bắt đầu</Form.Label>
                    <Form.Control
                      type="date"
                      value={editingTournament.start_date || ''}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        const updatedTournament = { ...editingTournament, start_date: newDate };
                        setEditingTournament(updatedTournament);
                        setEditingTournamentContent(JSON.stringify(updatedTournament, null, 2));
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

               <Form.Group className="mb-3">
                <Form.Label>Địa điểm</Form.Label>
                <Form.Control
                  type="text"
                  value={editingTournament.location || ''}
                  placeholder="Ví dụ: Nhà thi đấu ABC"
                  onChange={(e) => {
                    const newLocation = e.target.value;
                    const updatedTournament = { ...editingTournament, location: newLocation };
                    setEditingTournament(updatedTournament);
                    setEditingTournamentContent(JSON.stringify(updatedTournament, null, 2));
                  }}
                />
              </Form.Group>

              <hr />

              <Form.Group className="mb-3">
                <Form.Label>Nội dung JSON đầy đủ</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={15}
                  value={editingTournamentContent}
                  onChange={(e) => setEditingTournamentContent(e.target.value)}
                  onBlur={() => {
                     try {
                        const parsed = JSON.parse(editingTournamentContent);
                        setEditingTournament(parsed); // Sync the simple fields if JSON is valid
                      } catch (e) {
                        // Do nothing if JSON is invalid, keep the original simple field values
                      }
                  }}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Hủy
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Lưu thay đổi"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

    </Container>
  );
};

export default TournamentManagement;
